import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react"
import type {
  TemplateField,
  TemplateFieldDatasourceItem,
  TemplateFieldDatasourceDisplayColumn,
  TemplateSection,
} from "shared/api/templateService"
import { getFieldDatasourceOptions } from "shared/api/templateService"
import {
  isSearchSelectMultiple,
  tryParseJson,
  type ParsedFieldConfig,
} from "modules/permit/components/permit-request/utils/templateFieldUtils"

export type FieldDependencyInfo = {
  params: Record<string, string>
  dependencyValue?: string
  dependencyMissing: boolean
  config: ParsedFieldConfig | null
}

type DatasourceFieldRef = { field: TemplateField; cacheKey: string }

type DatasourceDefaultSelection = {
  code?: string
  value: string
  label?: string
}

interface UseDatasourceDependenciesParams {
  tenantId: string
  templateVersionId: string | null
  generalFields: TemplateField[]
  workflowSections: TemplateSection[]
  visibleWorkflowSections: TemplateSection[]
  isEditMode: boolean
  isReadOnly: boolean
  isStageEditor: boolean
  isReadOnlyViewer: boolean
  canApproveCurrentStage: boolean
  doesSectionMatchActiveStage: (section: TemplateSection) => boolean
  parseFieldConfig: (field: TemplateField) => ParsedFieldConfig | null
  getFieldDependencyInfo: (field: TemplateField) => FieldDependencyInfo
  resolveDatasourceStaticParams: (field: TemplateField) => Record<string, string>
  resolveFallbackDatasourceValue: (
    item?: TemplateFieldDatasourceItem | Record<string, unknown>,
  ) => string | undefined
  setTemplateFieldValue: (field: TemplateField, value: string | boolean | null | undefined) => void
  setTemplateFieldLabel: (field: TemplateField, label: string | null | undefined) => void
  getStoredTemplateFieldValue: (field: TemplateField) => string | undefined
  getStoredTemplateFieldLabel: (field: TemplateField) => string | undefined
}

interface UseDatasourceDependenciesResult {
  datasourceEnabledFields: DatasourceFieldRef[]
  loadDatasourceOptions: (field: TemplateField, parameters?: Record<string, string | undefined>) => Promise<void>
  getFieldOptions: (fieldId: string) => TemplateFieldDatasourceItem[]
  isFieldLoading: (fieldId: string) => boolean
  dependencyCacheRef: React.MutableRefObject<Record<string, string | undefined>>
  searchSelectLabels: Record<string, string>
  setSearchSelectLabels: Dispatch<SetStateAction<Record<string, string>>>
  setFieldOptions: Dispatch<SetStateAction<Record<string, TemplateFieldDatasourceItem[]>>>
}

export const useDatasourceDependencies = ({
  tenantId,
  templateVersionId,
  generalFields,
  workflowSections,
  visibleWorkflowSections,
  isEditMode,
  isReadOnly,
  isStageEditor,
  isReadOnlyViewer,
  canApproveCurrentStage,
  doesSectionMatchActiveStage,
  parseFieldConfig,
  getFieldDependencyInfo,
  resolveDatasourceStaticParams,
  resolveFallbackDatasourceValue,
  setTemplateFieldValue,
  setTemplateFieldLabel,
  getStoredTemplateFieldValue,
  getStoredTemplateFieldLabel,
}: UseDatasourceDependenciesParams): UseDatasourceDependenciesResult => {
  const parseDatasourceUiConfig = useCallback((field: TemplateField): Record<string, unknown> => {
    const parsed = tryParseJson<Record<string, unknown> | null>(field.datasource?.uiConfigJson)
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}
  }, [])

  const resolveRemoteSearchOnly = useCallback((field: TemplateField): boolean => {
    const rawValue = parseDatasourceUiConfig(field).remoteSearchOnly
    return rawValue === true || String(rawValue ?? "").trim().toLowerCase() === "true"
  }, [parseDatasourceUiConfig])

  const normalizeDefaultValue = useCallback((rawValue: unknown): string | undefined => {
    if (rawValue === null || rawValue === undefined) {
      return undefined
    }
    if (typeof rawValue === "string") {
      const trimmed = rawValue.trim()
      return trimmed.length > 0 ? trimmed : undefined
    }
    if (typeof rawValue === "number" || typeof rawValue === "boolean") {
      return String(rawValue)
    }
    return undefined
  }, [])

  const normalizeLooseKey = useCallback(
    (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, ""),
    [],
  )

  const resolveDatasourceColumnValue = useCallback(
    (item: TemplateFieldDatasourceItem, columnName: string): string | undefined => {
      const directColumns = item.columns ?? {}
      const normalizedColumnName = normalizeLooseKey(columnName)

      for (const [key, rawValue] of Object.entries(directColumns)) {
        if (normalizeLooseKey(key) !== normalizedColumnName) {
          continue
        }
        const normalizedValue = normalizeDefaultValue(rawValue)
        if (normalizedValue) {
          return normalizedValue
        }
      }

      if (normalizedColumnName === "value") {
        return normalizeDefaultValue(item.value)
      }

      if (normalizedColumnName === "label" || normalizedColumnName === "name") {
        return normalizeDefaultValue(item.label)
      }

      return undefined
    },
    [normalizeDefaultValue, normalizeLooseKey],
  )

  const resolveDefaultSelection = useCallback(
    (entry: unknown): DatasourceDefaultSelection | null => {
      const scalarValue = normalizeDefaultValue(entry)
      if (scalarValue) {
        return { value: scalarValue }
      }

      if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
        return null
      }

      const record = entry as Record<string, unknown>
      const value =
        normalizeDefaultValue(record.value) ??
        normalizeDefaultValue(record.id) ??
        normalizeDefaultValue(record.code)
      const code =
        normalizeDefaultValue(record.code) ??
        normalizeDefaultValue(record.defaultCode) ??
        normalizeDefaultValue(record.key)
      const label =
        normalizeDefaultValue(record.label) ??
        normalizeDefaultValue(record.text) ??
        normalizeDefaultValue(record.name)

      if (!value && !label) {
        return null
      }

      return {
        code,
        value: value ?? label ?? "",
        label: label ?? value ?? undefined,
      }
    },
    [normalizeDefaultValue],
  )

  const resolveDatasourceDefaultSelections = useCallback(
    (field: TemplateField): DatasourceDefaultSelection[] => {
      const uiConfig = parseDatasourceUiConfig(field)
      const fieldType = (field.fieldType ?? "").trim().toLowerCase()
      const isMultipleField = fieldType === "multiselect" || isSearchSelectMultiple(field)
      const selections: DatasourceDefaultSelection[] = []

      const pushSelection = (entry: unknown, splitCommaSeparated = false) => {
        if (splitCommaSeparated && typeof entry === "string" && entry.includes(",")) {
          entry
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
            .forEach((item) => {
              const selection = resolveDefaultSelection(item)
              if (selection) {
                selections.push(selection)
              }
            })
          return
        }

        const selection = resolveDefaultSelection(entry)
        if (selection) {
          selections.push(selection)
        }
      }

      if (Array.isArray(uiConfig.defaultValues)) {
        uiConfig.defaultValues.forEach((entry) => pushSelection(entry))
      } else if (uiConfig.defaultValues !== undefined) {
        pushSelection(uiConfig.defaultValues, isMultipleField)
      } else if (uiConfig.defaultValue !== undefined) {
        pushSelection(uiConfig.defaultValue, isMultipleField)
      } else if (Array.isArray(uiConfig.defaultCodes)) {
        uiConfig.defaultCodes.forEach((entry) => {
          const code = normalizeDefaultValue(entry)
          if (code) {
            selections.push({
              code,
              value: code,
              label: code,
            })
          }
        })
      } else if (uiConfig.defaultCodes !== undefined) {
        const rawCodes = uiConfig.defaultCodes
        if (isMultipleField && typeof rawCodes === "string" && rawCodes.includes(",")) {
          rawCodes
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
            .forEach((code) => {
              selections.push({
                code,
                value: code,
                label: code,
              })
            })
        } else {
          const code = normalizeDefaultValue(rawCodes)
          if (code) {
            selections.push({
              code,
              value: code,
              label: code,
            })
          }
        }
      } else if (uiConfig.defaultCode !== undefined) {
        const rawCode = normalizeDefaultValue(uiConfig.defaultCode)
        if (rawCode) {
          if (isMultipleField && rawCode.includes(",")) {
            rawCode
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item.length > 0)
              .forEach((code) => {
                selections.push({
                  code,
                  value: code,
                  label: code,
                })
              })
          } else {
            selections.push({
              code: rawCode,
              value: rawCode,
              label: rawCode,
            })
          }
        }
      }

      const uniqueSelections = new Map<string, DatasourceDefaultSelection>()
      selections.forEach((selection) => {
        const key = (selection.code ?? selection.value).trim().toLowerCase()
        if (key && !uniqueSelections.has(key)) {
          uniqueSelections.set(key, selection)
        }
      })

      return Array.from(uniqueSelections.values())
    },
    [parseDatasourceUiConfig, resolveDefaultSelection],
  )

  const hasDatasourceDefaultSelections = useCallback(
    (field: TemplateField) => resolveDatasourceDefaultSelections(field).length > 0,
    [resolveDatasourceDefaultSelections],
  )

  const resolveDefaultMatchColumn = useCallback(
    (field: TemplateField): string => {
      const uiConfig = parseDatasourceUiConfig(field)
      const configuredColumn = normalizeDefaultValue(uiConfig.defaultMatchColumn)
      return configuredColumn ?? "code"
    },
    [normalizeDefaultValue, parseDatasourceUiConfig],
  )

  const [fieldOptions, setFieldOptions] = useState<Record<string, TemplateFieldDatasourceItem[]>>({})
  const [fieldLoadingState, setFieldLoadingState] = useState<Record<string, boolean>>({})
  const [searchSelectLabels, setSearchSelectLabels] = useState<Record<string, string>>({})
  const dependencyCacheRef = useRef<Record<string, string | undefined>>({})
  const dependencyDebounceRef = useRef<Record<string, number | undefined>>({})
  const missingValueCacheRef = useRef<Record<string, string | undefined>>({})
  const datasourceRequestCacheRef = useRef<Record<string, string | undefined>>({})
  const defaultAppliedRef = useRef<Record<string, boolean | undefined>>({})
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
    Object.values(dependencyDebounceRef.current).forEach((timer) => {
      if (typeof timer === "number") {
        window.clearTimeout(timer)
      }
    })
  }, [])

  useEffect(() => {
    setFieldOptions({})
    setFieldLoadingState({})
    dependencyCacheRef.current = {}
    missingValueCacheRef.current = {}
    datasourceRequestCacheRef.current = {}
    defaultAppliedRef.current = {}
  }, [templateVersionId])

  const setFieldOptionsForField = useCallback(
    (fieldId: string, nextItems: TemplateFieldDatasourceItem[]) => {
      setFieldOptions((prev) => {
        const currentItems = prev[fieldId] ?? []

        if (currentItems === nextItems) {
          return prev
        }

        if (currentItems.length === nextItems.length) {
          const isSame =
            currentItems.length === 0 ||
            currentItems.every((item, index) => item === nextItems[index])

          if (isSame) {
            return prev
          }
        }

        return {
          ...prev,
          [fieldId]: nextItems,
        }
      })
    },
    [],
  )

  const datasourceEnabledFields = useMemo<DatasourceFieldRef[]>(() => {
    let autoIndex = 0
    const map = new Map<string, DatasourceFieldRef>()

    const registerField = (field?: TemplateField) => {
      if (!field) {
        return
      }
      const fieldType = (field.fieldType ?? "").toLowerCase()
      const isComputedField = fieldType === "computed"
      const requiresRemoteOptions =
        isComputedField ||
        fieldType === "select" ||
        fieldType === "multiselect" ||
        fieldType === "radio" ||
        fieldType === "search_select"

      const hasDatasource = Boolean(field.datasource)
      const hasStaticOptions = Array.isArray(field.options) && field.options.length > 0

      // ไม่มี datasource และไม่ใช่ computed -> ข้าม (select ที่มี options ตายตัวก็ไม่ต้องโหลด)
      if (!hasDatasource && !isComputedField) {
        return
      }

      const key = field.fieldId || field.code || `field-${autoIndex++}`
      if (!map.has(key)) {
        map.set(key, { field, cacheKey: key })
      }
    }

    // Always register general fields so read-only pages can still resolve search_select labels.
    generalFields.forEach(registerField)

    const workflowFieldSource = isStageEditor
      ? visibleWorkflowSections
      : isReadOnly
        ? visibleWorkflowSections
        : workflowSections.filter((section) => !section.approvalStageId)

    workflowFieldSource.forEach((section) => {
      section.fields?.forEach(registerField)
    })

    return Array.from(map.values())
  }, [
    generalFields,
    workflowSections,
    visibleWorkflowSections,
    isReadOnly,
    isStageEditor,
    isReadOnlyViewer,
  ])

  useEffect(() => {
    const activeFieldIds = new Set(
      datasourceEnabledFields
        .map(({ field }) => field.fieldId?.trim())
        .filter((key): key is string => Boolean(key)),
    )

    setFieldOptions((prev) => {
      const entries = Object.entries(prev).filter(([key]) => activeFieldIds.has(key))
      if (entries.length === Object.keys(prev).length) {
        return prev
      }
      return entries.reduce<Record<string, TemplateFieldDatasourceItem[]>>(
        (acc, [key, value]) => {
          acc[key] = value
          return acc
        },
        {},
      )
    })

    setFieldLoadingState((prev) => {
      const entries = Object.entries(prev).filter(([key]) => activeFieldIds.has(key))
      if (entries.length === Object.keys(prev).length) {
        return prev
      }
      return entries.reduce<Record<string, boolean>>((acc, [key, value]) => {
        acc[key] = value
        return acc
      }, {})
    })

    Object.keys(dependencyCacheRef.current).forEach((key) => {
      if (key && !activeFieldIds.has(key)) {
        delete dependencyCacheRef.current[key]
        delete datasourceRequestCacheRef.current[key]
        delete defaultAppliedRef.current[key]
        if (dependencyDebounceRef.current[key]) {
          window.clearTimeout(dependencyDebounceRef.current[key])
          dependencyDebounceRef.current[key] = undefined
        }
      }
    })
  }, [datasourceEnabledFields])

  const loadDatasourceOptions = useCallback(
    async (field: TemplateField, parameters: Record<string, string | undefined> = {}) => {
      // หากไม่ผูก datasource (และไม่ใช่ computed) ไม่ต้องโหลด
      if (!field.datasource && (field.fieldType ?? "").toLowerCase() !== "computed") {
        return
      }
      const payload: Record<string, string> = {}
      const staticParams = resolveDatasourceStaticParams(field)
      const mergedParams = {
        ...staticParams,
        tenant: tenantId,
        tenantId,
        tenant_id: tenantId,
        ...parameters,
      }

      Object.entries(mergedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          payload[key] = value
        }
      })
      const fieldType = (field.fieldType ?? "").toLowerCase()
      const isComputedField = fieldType === "computed"
      const remoteSearchOnly = resolveRemoteSearchOnly(field)
      const shouldLoadAllPages =
        !remoteSearchOnly && (fieldType === "select" || fieldType === "radio" || fieldType === "multiselect")
      const pageSize = shouldLoadAllPages ? 10 : 10
      const storedValue = getStoredTemplateFieldValue(field)
      const selectedValues =
        storedValue && (fieldType === "multiselect" || isSearchSelectMultiple(field))
          ? storedValue
              .split(",")
              .map((value) => value.trim())
              .filter((value) => value.length > 0)
          : storedValue
            ? [storedValue]
            : []
      const defaultSelections =
        !isEditMode &&
        !storedValue?.trim() &&
        !defaultAppliedRef.current[field.fieldId]
          ? resolveDatasourceDefaultSelections(field)
          : []
      const defaultMatchColumn = resolveDefaultMatchColumn(field)
      const requestSelectedValues =
        selectedValues.length > 0
          ? selectedValues
          : defaultSelections
              .filter((item) => !item.code)
              .map((item) => item.value)
      const requestSignature = JSON.stringify({
        payload,
        selectedValues: requestSelectedValues.map((value) => value.trim().toLowerCase()),
      })

      if (datasourceRequestCacheRef.current[field.fieldId] === requestSignature) {
        return
      }

      datasourceRequestCacheRef.current[field.fieldId] = requestSignature
      setFieldLoadingState((prev) => ({ ...prev, [field.fieldId]: true }))

      try {
        const datasourceResponse = await getFieldDatasourceOptions(
          field.fieldId,
          payload,
          undefined,
          1,
          pageSize,
          requestSelectedValues,
        )
        const dedupeByValue = (items: TemplateFieldDatasourceItem[]) => {
          const map = new Map<string, TemplateFieldDatasourceItem>()
          items.forEach((item) => {
            const key = (item?.value ?? "").trim().toLowerCase()
            if (key) {
              map.set(key, item)
            }
          })
          return Array.from(map.values())
        }
        // The datasource query can return the same value more than once (e.g. a
        // backing SQL join that fans out rows) - dedupe up front so every path
        // below (fast path and the merge-with-lookup fallbacks) stays unique,
        // instead of only relying on mergeUniqueItems, which is skipped entirely
        // when the initial response already satisfies the selected values.
        let resolvedItems = dedupeByValue(datasourceResponse.items ?? [])
        const mergeUniqueItems = (base: TemplateFieldDatasourceItem[], incoming: TemplateFieldDatasourceItem[]) => {
          const map = new Map<string, TemplateFieldDatasourceItem>()
          base.forEach((item) => {
            const key = (item?.value ?? "").trim().toLowerCase()
            if (key) {
              map.set(key, item)
            }
          })
          incoming.forEach((item) => {
            const key = (item?.value ?? "").trim().toLowerCase()
            if (key && !map.has(key)) {
              map.set(key, item)
            }
          })
          return Array.from(map.values())
        }
        if (shouldLoadAllPages && !isComputedField) {
          const maxItems = 10
          const maxPages = Math.max(1, Math.ceil(maxItems / pageSize))
          const totalPages = Math.min(
            datasourceResponse.totalPages || 1,
            maxPages,
          )
          if (totalPages > 1) {
            const itemMap = new Map<string, TemplateFieldDatasourceItem>()
            resolvedItems.forEach((item) => {
              if (item?.value) {
                itemMap.set(item.value, item)
              }
            })
            for (let page = 2; page <= totalPages; page += 1) {
              const pagedResponse = await getFieldDatasourceOptions(
                field.fieldId,
                payload,
                undefined,
                page,
                pageSize,
                requestSelectedValues,
              )
              const nextItems = pagedResponse.items ?? []
              nextItems.forEach((item) => {
                if (item?.value && !itemMap.has(item.value)) {
                  itemMap.set(item.value, item)
                }
              })
            }
            resolvedItems = Array.from(itemMap.values())
          }
        }

        if (!isComputedField && requestSelectedValues.length > 0) {
          const normalizedSelectedValues = requestSelectedValues
            .map((item) => item.trim().toLowerCase())
            .filter((item) => item.length > 0)
          const hasAllSelectedValues = normalizedSelectedValues.every((selected) =>
            resolvedItems.some((item) => (item?.value ?? "").trim().toLowerCase() === selected),
          )

          // Fallback: some datasources ignore selectedValues in the first call.
          // Query again with search term by selected value to resolve display labels.
          if (!hasAllSelectedValues) {
            for (const selected of normalizedSelectedValues.slice(0, 10)) {
              const selectedOriginal =
                requestSelectedValues.find((candidate) => candidate.trim().toLowerCase() === selected) ?? selected
              const selectedLookupResponse = await getFieldDatasourceOptions(
                field.fieldId,
                payload,
                selectedOriginal,
                1,
                pageSize,
                [selectedOriginal],
              )
              resolvedItems = mergeUniqueItems(resolvedItems, selectedLookupResponse.items ?? [])
            }
          }
        }

        if (!isComputedField && defaultSelections.some((item) => item.code)) {
          const normalizedMatchColumn = normalizeLooseKey(defaultMatchColumn)
          const unresolvedCodeSelections = defaultSelections.filter((selection) => {
            const normalizedCode = (selection.code ?? "").trim().toLowerCase()
            if (!normalizedCode) {
              return false
            }

            return !resolvedItems.some((item) => {
              const matchedColumnValue = resolveDatasourceColumnValue(item, defaultMatchColumn)
              if (matchedColumnValue && matchedColumnValue.trim().toLowerCase() === normalizedCode) {
                return true
              }

              if (normalizedMatchColumn === "value") {
                return (item.value ?? "").trim().toLowerCase() === normalizedCode
              }

              if (normalizedMatchColumn === "label" || normalizedMatchColumn === "name") {
                return (item.label ?? "").trim().toLowerCase() === normalizedCode
              }

              return false
            })
          })

          for (const selection of unresolvedCodeSelections.slice(0, 10)) {
            const lookupCode = (selection.code ?? "").trim()
            if (!lookupCode) {
              continue
            }

            const selectedLookupResponse = await getFieldDatasourceOptions(
              field.fieldId,
              payload,
              lookupCode,
              1,
              pageSize,
            )
            resolvedItems = mergeUniqueItems(resolvedItems, selectedLookupResponse.items ?? [])
          }
        }

        if (!isMountedRef.current) return
        if (isComputedField) {
          const primaryItem = resolvedItems?.[0]
          const computedValue = resolveFallbackDatasourceValue(primaryItem)
          const normalizedValue = computedValue?.trim()
          if (normalizedValue) {
            setTemplateFieldValue(field, normalizedValue)
          } else if (!getStoredTemplateFieldValue(field)) {
            setTemplateFieldValue(field, null)
          }
        } else {
          setFieldOptionsForField(field.fieldId, resolvedItems)

          if (storedValue?.trim()) {
            defaultAppliedRef.current[field.fieldId] = true
          } else if (!isEditMode && !defaultAppliedRef.current[field.fieldId] && defaultSelections.length > 0) {
            const matchedSelectionMap = new Map<string, TemplateFieldDatasourceItem>()
            resolvedItems.forEach((item) => {
              const key = (item.value ?? "").trim().toLowerCase()
              if (key) {
                matchedSelectionMap.set(key, item)
              }
            })

            const resolvedDefaultSelections = defaultSelections
              .map((selection) => {
                const codeKey = (selection.code ?? "").trim().toLowerCase()
                const valueKey = selection.value.trim().toLowerCase()
                const matchedItem =
                  (codeKey
                    ? resolvedItems.find((item) => {
                        const matchedColumnValue = resolveDatasourceColumnValue(item, defaultMatchColumn)
                        return (
                          matchedColumnValue?.trim().toLowerCase() === codeKey ||
                          (normalizeLooseKey(defaultMatchColumn) === "value" &&
                            (item.value ?? "").trim().toLowerCase() === codeKey) ||
                          ((normalizeLooseKey(defaultMatchColumn) === "label" ||
                            normalizeLooseKey(defaultMatchColumn) === "name") &&
                            (item.label ?? "").trim().toLowerCase() === codeKey)
                        )
                      })
                    : undefined) ?? matchedSelectionMap.get(valueKey)

                return {
                  value: matchedItem?.value ?? selection.value,
                  label: matchedItem?.label ?? selection.label ?? matchedItem?.value ?? selection.value,
                }
              })
              .filter((selection) => selection.value.trim().length > 0)

            if (resolvedDefaultSelections.length > 0) {
              if (fieldType === "multiselect" || isSearchSelectMultiple(field)) {
                const nextValue = resolvedDefaultSelections.map((item) => item.value).join(",")
                setTemplateFieldValue(field, nextValue)

                if (fieldType === "search_select") {
                  const nextLabel = resolvedDefaultSelections.map((item) => item.label).join(", ")
                  const nextLabelMap = resolvedDefaultSelections.reduce<Record<string, string>>((acc, item) => {
                    const key = item.value.trim().toLowerCase()
                    if (key) {
                      acc[key] = item.label
                    }
                    return acc
                  }, {})
                  setTemplateFieldLabel(field, nextLabel || null)
                  setSearchSelectLabels((prev) => ({
                    ...prev,
                    [field.fieldId]: JSON.stringify(nextLabelMap),
                  }))
                }
              } else {
                const firstSelection = resolvedDefaultSelections[0]
                setTemplateFieldValue(field, firstSelection.value)

                if (fieldType === "search_select") {
                  setTemplateFieldLabel(field, firstSelection.label ?? firstSelection.value)
                  setSearchSelectLabels((prev) => ({
                    ...prev,
                    [field.fieldId]: firstSelection.label ?? firstSelection.value,
                  }))
                }
              }

              defaultAppliedRef.current[field.fieldId] = true
            }
          }
        }
      } catch {
        if (!isMountedRef.current) return
        if (isComputedField) {
          if (!getStoredTemplateFieldValue(field)) {
            setTemplateFieldValue(field, null)
          }
        } else {
          setFieldOptionsForField(field.fieldId, [])
        }
      } finally {
        if (!isMountedRef.current) return
        setFieldLoadingState((prev) => ({ ...prev, [field.fieldId]: false }))
      }
    },
    [
      getStoredTemplateFieldValue,
      isEditMode,
      normalizeLooseKey,
      resolveRemoteSearchOnly,
      resolveDatasourceColumnValue,
      resolveDefaultMatchColumn,
      resolveDatasourceDefaultSelections,
      resolveDatasourceStaticParams,
      setFieldOptionsForField,
      setTemplateFieldLabel,
      setSearchSelectLabels,
      tenantId,
      setTemplateFieldValue,
    ],
  )

  useEffect(() => {
    if (datasourceEnabledFields.length === 0) return

    datasourceEnabledFields.forEach(({ field }) => {
      const config = parseFieldConfig(field)
      const fieldType = (field.fieldType ?? "").toLowerCase()
      const remoteSearchOnly = resolveRemoteSearchOnly(field)
      if (config?.dependsOn) {
        return
      }
      if (remoteSearchOnly && fieldType !== "computed" && !hasDatasourceDefaultSelections(field)) {
        return
      }

      // ถ้าโหลดมาแล้วและมี options อยู่แล้ว ไม่ต้อง preload ซ้ำ
      const cached = fieldOptions[field.fieldId]
      if (cached && cached.length > 0) {
        return
      }

      loadDatasourceOptions(field)
    })
  }, [
    datasourceEnabledFields,
    fieldOptions,
    hasDatasourceDefaultSelections,
    loadDatasourceOptions,
    parseFieldConfig,
    resolveRemoteSearchOnly,
  ])

  useEffect(() => {
    if (datasourceEnabledFields.length === 0) return

    datasourceEnabledFields.forEach(({ field }) => {
      const { params, dependencyValue, dependencyMissing, config } = getFieldDependencyInfo(field)
      const fieldType = (field.fieldType ?? "").toLowerCase()
      const isComputedField = fieldType === "computed"
      const previousCacheKey = dependencyCacheRef.current[field.fieldId]
      const hadCachedDependencies = Boolean(previousCacheKey)
      const hasStoredValue = Boolean(getStoredTemplateFieldValue(field)?.trim())

      if (!config?.dependsOn) {
        // ถ้าเป็นฟิลด์ไม่มี dependency และมี cache ของ options อยู่แล้ว ไม่ต้องโหลดซ้ำ
        if (!isComputedField && fieldOptions[field.fieldId]?.length) {
          return
        }
        return
      }

      if (!dependencyValue) {
        dependencyCacheRef.current[field.fieldId] = undefined
        if (dependencyDebounceRef.current[field.fieldId]) {
          window.clearTimeout(dependencyDebounceRef.current[field.fieldId])
          dependencyDebounceRef.current[field.fieldId] = undefined
        }
        if (isComputedField && hadCachedDependencies) {
          setTemplateFieldValue(field, null)
        }
        if (config.dependencyConfig?.requiredForLoad) {
          if (isReadOnly && hasStoredValue) {
            loadDatasourceOptions(field)
            return
          }
          setFieldOptionsForField(field.fieldId, [])
          return
        }
        // ถ้าไม่บังคับ dependency ให้โหลด options แบบไม่กรอง (ช่วยแสดงค่าเดิม)
        if (!fieldOptions[field.fieldId]?.length) {
          loadDatasourceOptions(field)
        }
        return
      }

      if (dependencyMissing) {
        dependencyCacheRef.current[field.fieldId] = undefined
        if (dependencyDebounceRef.current[field.fieldId]) {
          window.clearTimeout(dependencyDebounceRef.current[field.fieldId])
          dependencyDebounceRef.current[field.fieldId] = undefined
        }
        if (isComputedField && hadCachedDependencies) {
          setTemplateFieldValue(field, null)
        }
        if (isReadOnly && hasStoredValue) {
          loadDatasourceOptions(field)
          return
        }
        setFieldOptionsForField(field.fieldId, [])
        return
      }

      const cacheKey = JSON.stringify(params)
      if (dependencyCacheRef.current[field.fieldId] === cacheKey) {
        return
      }

      dependencyCacheRef.current[field.fieldId] = cacheKey
      if (dependencyDebounceRef.current[field.fieldId]) {
        window.clearTimeout(dependencyDebounceRef.current[field.fieldId])
      }
      dependencyDebounceRef.current[field.fieldId] = window.setTimeout(() => {
        loadDatasourceOptions(field, params)
        dependencyDebounceRef.current[field.fieldId] = undefined
      }, 300)
    })
  }, [
    datasourceEnabledFields,
    getFieldDependencyInfo,
    getStoredTemplateFieldValue,
    isReadOnly,
    loadDatasourceOptions,
    setFieldOptionsForField,
    setTemplateFieldValue,
  ])

  useEffect(() => {
    if (datasourceEnabledFields.length === 0) return

    datasourceEnabledFields.forEach(({ field }) => {
      const fieldId = field.fieldId
      if (!fieldId) {
        return
      }
      const fieldType = (field.fieldType ?? "").toLowerCase()
      const isDatasourceMultiSelect =
        (fieldType === "multiselect" || isSearchSelectMultiple(field)) &&
        Boolean(field.datasource)
      if (isDatasourceMultiSelect) {
        return
      }
      if (resolveRemoteSearchOnly(field)) {
        return
      }
      if (fieldType === "computed") {
        return
      }
      const storedValue = getStoredTemplateFieldValue(field)
      if (!storedValue) {
        if (missingValueCacheRef.current[fieldId]) {
          delete missingValueCacheRef.current[fieldId]
        }
        return
      }

      const selectedValues =
        fieldType === "multiselect" || isSearchSelectMultiple(field)
          ? storedValue
              .split(",")
              .map((value) => value.trim())
              .filter((value) => value.length > 0)
          : [storedValue]

      if (selectedValues.length === 0) {
        return
      }

      const options = fieldOptions[fieldId] ?? []
      const hasAllValues = selectedValues.every((value) =>
        options.some(
          (option) => (option.value ?? "").trim().toLowerCase() === value.trim().toLowerCase(),
        ),
      )
      if (hasAllValues) {
        if (missingValueCacheRef.current[fieldId]) {
          delete missingValueCacheRef.current[fieldId]
        }
        return
      }

      const { params, dependencyMissing, config } = getFieldDependencyInfo(field)
      if (config?.dependsOn && dependencyMissing) {
        return
      }

      const cacheKey = JSON.stringify({ selectedValues, params })
      if (missingValueCacheRef.current[fieldId] === cacheKey) {
        return
      }
      missingValueCacheRef.current[fieldId] = cacheKey
      loadDatasourceOptions(field, params)
    })
  }, [
    datasourceEnabledFields,
    fieldOptions,
    getFieldDependencyInfo,
    getStoredTemplateFieldValue,
    loadDatasourceOptions,
    resolveRemoteSearchOnly,
  ])

  const getFieldOptions = useCallback(
    (fieldId: string): TemplateFieldDatasourceItem[] => fieldOptions[fieldId] ?? [],
    [fieldOptions],
  )

  // Ensure we display existing values even if options have not loaded yet
  useEffect(() => {
    if (datasourceEnabledFields.length === 0) return

    setSearchSelectLabels((prev) => {
      let next = prev
      const isGuidLike = (value: string) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim())

      datasourceEnabledFields.forEach(({ field }) => {
        const fieldType = (field.fieldType ?? "").toLowerCase()
        if (fieldType !== "search_select") {
          return
        }
        const storedValue = getStoredTemplateFieldValue(field)
        if (!storedValue) {
          return
        }
        const key = field.fieldId ?? field.code ?? ''
        if (!key) {
          return
        }
        const storedLabel = getStoredTemplateFieldLabel(field)?.trim()
        const currentLabel = next[key]?.trim()
        const storedValueTrimmed = storedValue.trim()
        const currentLooksUnresolved =
          !currentLabel ||
          currentLabel.toLowerCase() === storedValueTrimmed.toLowerCase() ||
          isGuidLike(currentLabel)

        if (currentLabel !== undefined && (!storedLabel || !currentLooksUnresolved)) {
          return
        }

        if (next === prev) {
          next = { ...prev }
        }
        next[key] = storedLabel || storedValue
      })
      return next
    })
  }, [datasourceEnabledFields, getStoredTemplateFieldLabel, getStoredTemplateFieldValue])

  const isFieldLoading = useCallback(
    (fieldId: string) => fieldLoadingState[fieldId] ?? false,
    [fieldLoadingState],
  )

  return {
    datasourceEnabledFields,
    loadDatasourceOptions,
    getFieldOptions,
    isFieldLoading,
    dependencyCacheRef,
    searchSelectLabels,
    setSearchSelectLabels,
    setFieldOptions,
  }
}
